import {
  PENDING_AMAZON_SELLER_ACCOUNTS_ACTION,
  SET_AMAZON_SELLER_ACCOUNTS_DATA,
  SET_AMAZON_SELLER_ACCOUNT_SELECTOR_DATA
} from 'constants/action_types/amazon_seller_accounts'
import {
  ACCOUNT_AMAZON_SELLER_SET_HAS_PPC_ACCESS,
  ACCOUNT_AMAZON_SELLER_ACCOUNT_PATCH_DETAILS_COMPLETE,
  ACCOUNT_AMAZON_SELLER_ACCOUNT_DESTROY_COMPLETE
} from 'constants/action_types/accounts/amazonSellerAccounts'
import { SELECT_AMAZON_SELLER_ACCOUNT } from 'constants/action_types/global'
import { SELECTED_ACCOUNT_MARKETPLACE_KEY } from 'constants/local_storage'

import { removeNonAmazonMarketplaces } from 'helpers/mws'
import { getLocalData, removeLocalData, setLocalData } from 'helpers/storage'

export const initialState = {
  isLoading: false,

  amazonSellerAccounts: [],

  amazonSellerAccountSelector: {
    selectedAccount: undefined,
    selectedCountry: undefined,
    isLoading: false
  },

  sellerDataAvailable: true
}

function saveSelectedMarketplaceToStorage(accountId, countryCode = null) {
  // Don't save if we don't have a valid country code
  if (!accountId) {
    return
  }

  try {
    const selection = { accountId, countryCode }
    setLocalData(SELECTED_ACCOUNT_MARKETPLACE_KEY, JSON.stringify(selection))
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}

function getSelectedMarketplaceFromStorage() {
  try {
    const stored = getLocalData(SELECTED_ACCOUNT_MARKETPLACE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    // Clear invalid data and return null
    removeLocalData(SELECTED_ACCOUNT_MARKETPLACE_KEY)
    return null
  }
}

function clearSelectedMarketplaceFromStorage() {
  try {
    removeLocalData(SELECTED_ACCOUNT_MARKETPLACE_KEY)
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}

function findStoredAccountAndCountry(accounts) {
  const stored = getSelectedMarketplaceFromStorage()

  if (!stored) {
    return null
  }

  const account = accounts.find(acc => acc.id === stored.accountId)
  if (!account) {
    clearSelectedMarketplaceFromStorage()
    return null
  }

  const country = account.countries.find(
    c => c.country_code === stored.countryCode
  )

  // sync_pending accounts don't have marketplaces; everyone else must have
  // marketplacees so if there's no country found we must invalidate local storage selection
  if (account.aasm_state !== 'sync_pending' && !country) {
    clearSelectedMarketplaceFromStorage()
    return null
  }

  return { account, country }
}

function removeAmazonSellerAccount(state, accountId) {
  const result = { ...state }

  result.amazonSellerAccounts = result.amazonSellerAccounts.filter(
    account => account.id !== accountId
  )

  return result
}

function findDefaultAccountAndCountry(accounts) {
  const accountWithDefault = accounts.find(
    account =>
      account.countries &&
      account.countries.length > 0 &&
      account.countries.some(country => country.is_default)
  )

  if (!accountWithDefault) {
    return null
  }

  const defaultCountry = accountWithDefault.countries.find(
    country => country.is_default
  )
  return { account: accountWithDefault, country: defaultCountry }
}

function getFirstAvailableAccountAndCountry(accounts) {
  const [firstAccount] = accounts

  // Safety check for accounts with no countries
  if (!firstAccount.countries || firstAccount.countries.length === 0) {
    return null
  }

  const [firstCountry] = firstAccount.countries
  return { account: firstAccount, country: firstCountry }
}

function hasValidAccounts(accounts) {
  return (
    Array.isArray(accounts) &&
    accounts.length > 0 &&
    (accounts[0].countries.length > 0 ||
      accounts[0].aasm_state === 'sync_pending')
  )
}

function createEmptyAccountSelector() {
  return {
    selectedAccount: undefined,
    selectedCountry: undefined,
    isLoading: false,
    error: false
  }
}

function setActiveMarketplace(state, accounts) {
  if (!hasValidAccounts(accounts)) {
    // If we don't have accounts, we need to revert this to new values
    // otherwise it will maintain the ones from the last session
    return createEmptyAccountSelector()
  }

  // Try to restore from localStorage first
  const storedSelection = findStoredAccountAndCountry(accounts)
  if (storedSelection) {
    return {
      ...state.amazonSellerAccountSelector,
      error: false,
      selectedAccount: storedSelection.account,
      selectedCountry: storedSelection.country
    }
  }

  // Fall back to default selection logic
  const defaultSelection = findDefaultAccountAndCountry(accounts)
  const selection =
    defaultSelection || getFirstAvailableAccountAndCountry(accounts)

  // If we still don't have a valid selection, return empty state
  if (!selection) {
    return createEmptyAccountSelector()
  }

  // Save the selection to localStorage for next time
  saveSelectedMarketplaceToStorage(
    selection.account.id,
    selection.country?.country_code
  )

  return {
    ...state.amazonSellerAccountSelector,
    error: false,
    selectedAccount: selection.account,
    selectedCountry: selection.country
  }
}

function amazonSellerAccounts(state = initialState, action) {
  switch (action.type) {
    case PENDING_AMAZON_SELLER_ACCOUNTS_ACTION:
      return {
        ...state,
        isLoading: true
      }
    case SET_AMAZON_SELLER_ACCOUNTS_DATA:
      return {
        ...state,
        amazonSellerAccounts: action.payload.amazonSellerAccounts,
        amazonSellerAccountSelector:
          action.payload.amazonSellerAccountSelector ||
          action.state?.amazonSellerAccountSelector ||
          setActiveMarketplace(state, action.payload.amazonSellerAccounts),
        isLoading: false,
        sellerDataAvailable: action.payload.sellerDataAvailable
      }
    case SET_AMAZON_SELLER_ACCOUNT_SELECTOR_DATA:
      return {
        ...state,
        amazonSellerAccountSelector: action.payload
      }
    case SELECT_AMAZON_SELLER_ACCOUNT: {
      // Save the new selection to localStorage
      saveSelectedMarketplaceToStorage(
        action.payload.account?.id,
        action.payload.country?.country_code
      )

      return {
        ...state,
        amazonSellerAccountSelector: {
          ...state.amazonSellerAccountSelector,
          selectedAccount: action.payload.account,
          selectedCountry: action.payload.country
        }
      }
    }
    case ACCOUNT_AMAZON_SELLER_SET_HAS_PPC_ACCESS: {
      const accountId = action.payload
      let amazonSellerAccountSelector = { ...state.amazonSellerAccountSelector }

      if (state.amazonSellerAccountSelector.selectedAccount.id === accountId) {
        // update currently selected account
        amazonSellerAccountSelector = {
          ...state.amazonSellerAccountSelector,
          selectedAccount: {
            ...state.amazonSellerAccountSelector.selectedAccount,
            has_ppc_access: true
          }
        }
      }

      // update account entry in an array of seller accounts
      const amazonSellerAccounts = state.amazonSellerAccounts.map(account => {
        if (account.id === accountId) {
          account = {
            ...account,
            has_ppc_access: true
          }
        }
        return account
      })

      // return state with updated entries
      return {
        ...state,
        amazonSellerAccountSelector,
        amazonSellerAccounts
      }
    }
    case ACCOUNT_AMAZON_SELLER_ACCOUNT_PATCH_DETAILS_COMPLETE: {
      const amazonSellerAccounts = state.amazonSellerAccounts.map(account => {
        if (account.id !== action.payload.id) {
          return {
            ...account,
            // if the returned account is now the default - unset the default account on everything else
            is_default: action.payload.is_default ? false : account.is_default,
            countries: account.countries.map(country => {
              // if the account isn't default - no point checking each country - just clear them out
              if (action.payload.is_default) {
                country.is_default = false
              }
              return country
            })
          }
        }
        const filteredData = removeNonAmazonMarketplaces(action.payload)
        return {
          ...account,
          ...filteredData
        }
      })

      return {
        ...state,
        amazonSellerAccountSelector: setActiveMarketplace(
          state,
          amazonSellerAccounts
        ),
        amazonSellerAccounts
      }
    }
    case ACCOUNT_AMAZON_SELLER_ACCOUNT_DESTROY_COMPLETE:
      return removeAmazonSellerAccount(state, action.payload.accountId)
    default:
      return state
  }
}

export default amazonSellerAccounts
